import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaSave,
  FaTimesCircle,
  FaBarcode,
  FaMinus,
  FaPlus,
} from 'react-icons/fa';

import { useAuth } from '~/hooks';
import * as Yup from 'yup';

import api from '~/services/api';

import { Input, Select } from '~/components/Form';
import { TableLoading } from '~/components/Table';

import { Container, Header, Controls } from '~/styles/components';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import ConfirmWindow from '~/components/ConfirmWindow';
import { confirmAlert } from 'react-confirm-alert';
import formatValueOnChange from '~/util/formatValueOnChange';
import formatValue from '~/util/formatValue';
import { Content, BilletForm, Finances, Finance } from './styles';

const Edit = ({ match }) => {
  const { company } = useAuth();
  const formRef = useRef(null);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [monthlyPayment, setMonthlyPayment] = useState({});
  const [clientsOptions, setClientsOptions] = useState([]);
  const [eventsOptions, seteventsOptions] = useState([]);
  const [financialOptions, setFinancialOptions] = useState([]);

  const [finance, setFinance] = useState([]);

  const loadFinancialData = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/old-database/financial-data`, {
          params: {
            company_id: company.old_id,
          },
        });

        const options = response.data.map(item => ({
          value: item.id,
          label: item.title,
        }));

        setFinancialOptions(options);
      } catch (error) {
        toast.error(
          `Falha ao buscar dados financeiros: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const loadClients = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/relationships`, {
          params: {
            company_id: company.id,
            selectOnly: true,
            active: true,
            type: 1,
          },
        });

        const options = response.data.map(item => ({
          value: item.old_id,
          label: item.name,
        }));

        setClientsOptions(options);
      } catch (error) {
        toast.error(
          `Falha ao buscar clientes: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const loadEvents = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get('/old-database/events', {
          params: {
            company: company.id,
            monthly_payments: true,
            billet: true,
          },
        });

        if (response.data) {
          const options = response.data.map(event => ({
            value: event.id,
            label: event.name_label,
          }));
          seteventsOptions(options);
        }
      } catch (error) {
        toast.error(
          `Falha ao buscar eventos: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  const loadMonthlyPayment = useCallback(async () => {
    if (company) {
      try {
        setLoading(true);

        const response = await api.get(`/monthly-payments/${match.params.id}`, {
          params: {
            company_id: company.old_id,
          },
        });

        if (response.data) {
          const { data } = response;

          // Validar a espécie
          const validatedSpecies = [2, 4].includes(data.species)
            ? data.species
            : 4;

          setMonthlyPayment({
            ...data,
            price: formatValue(data.price),
            species: validatedSpecies,
          });

          // Carregar os registros financeiros
          if (data.monthly_payment_finance) {
            const formattedFinance = data.monthly_payment_finance.map(item => ({
              temp_id: `existing_${item.id}`,
              id: item.id,
              eref: item.eref || '1.018.015',
              account: item.account || item.event_id || item.event?.id || '',
              name: item.name || '',
              competence: item.competence || null,
              price: formatValue(item.price || 0),
              document: item.document || null,
              note: item.note || null,
              date_to_allow: item.date_to_allow || null,
              company_id: data.company_id,
              eidref: data.id,
              mainid: item.mainid || 1,
              date: new Date(),
              financial: data.financial,
              status: item.status !== undefined ? item.status : false,
              event: item.event || null,
            }));
            setFinance(formattedFinance);
          }
        } else {
          toast.warn('Nenhuma mensalidade foi encontrada.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      } catch (error) {
        toast.error(
          `Falha ao buscar mensalidade: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      } finally {
        setLoading(false);
      }
    }
  }, [company, match]);

  const totalFinancePrice = useMemo(() => {
    return finance
      .filter(item => !item.status) // Considerar apenas itens não removidos
      .reduce((total, item) => {
        const price = parseFloat(
          item.price?.replace(/[^\d,]/g, '').replace(',', '.')
        );
        const result = total + (isNaN(price) ? 0 : price);
        return result;
      }, 0);
  }, [finance]);

  useEffect(() => {
    setMonthlyPayment(prevMonthlyPayment => ({
      ...prevMonthlyPayment,
      price: formatValue(totalFinancePrice),
    }));
  }, [totalFinancePrice]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadFinancialData(), loadClients(), loadEvents()]);
      await loadMonthlyPayment();
    };

    loadData();
  }, [loadFinancialData, loadClients, loadEvents, loadMonthlyPayment]);

  const monthlyPaymentSchema = Yup.object().shape({
    drawee: Yup.string().required('Cliente é obrigatório'),
    price: Yup.string().required('Preço é obrigatório'),
    emition_day: Yup.number().required('Dia de emissão é obrigatório'),
    financial: Yup.string().required('Dados financeiros é obrigatório'),
    expiration_day: Yup.number().required('Dia de vencimento é obrigatório'),
    interest: Yup.number().required('Juros é obrigatório'),
    fine: Yup.number().required('Multa é obrigatória'),
    days_to_fine: Yup.number().required('Dias para multar é obrigatório'),
    protest: Yup.number().required('Protesto é obrigatório'),
    message: Yup.string().nullable(),
    species: Yup.number().required('Espécie é obrigatória'),
  });

  const handleSave = useCallback(async () => {
    try {
      const monthly_payment_finance = finance.map(item => ({
        ...item,
        // Mantém todos os valores originais sem preenchimento automático
        id: item.id || null,
        eref: item.eref || '1.018.015',
        eidref: item.eidref || monthlyPayment.id,
        company_id: item.company_id || company.old_id,
        mainid: item.mainid || 1,
        account: item.account || null,
        document: item.document || null,
        name: item.name || null,
        competence: null, // Garantindo que competence será sempre null
        note: item.note || null,
        date_to_allow: item.date_to_allow || null,
        price: item.price?.replace(/[^\d,]/g, '').replace(',', '.') || '0',
        status: item.status,
        temp_id: null,
      }));

      const formattedMonthlyPayment = {
        ...monthlyPayment,
        price: monthlyPayment.price?.replace(/[^\d,]/g, '').replace(',', '.'),
        monthly_payment_finance,
      };

      await monthlyPaymentSchema.validate(formattedMonthlyPayment, {
        abortEarly: false,
      });

      await api.put(
        `/monthly-payments/${match.params.id}`,
        formattedMonthlyPayment
      );

      toast.success('Mensalidade alterada com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      if (history.length > 1 && history.action !== 'REPLACE') {
        history.goBack();
      } else {
        history.push('/monthly-payments');
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
          toast.error(err.message, {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        });
        formRef.current.setErrors(validationErrors);
      } else {
        toast.error(
          `Falha ao salvar mensalidade: ${JSON.stringify(
            error.response?.data?.error
          ) || error.message}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [
    monthlyPayment,
    monthlyPaymentSchema,
    finance,
    history,
    match.params.id,
    company.old_id,
  ]);

  const confirmSaveForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleSave}
            onClose={onClose}
            message="Todos os dados serão salvos com esta ação."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleSave]);

  const handleCancel = useCallback(() => {
    if (history.length > 1 && history.action !== 'REPLACE') {
      history.goBack();
    } else {
      history.push('/monthly-payments');
    }
  }, [history]);

  const confirmCalcelForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return <ConfirmWindow onClick={handleCancel} onClose={onClose} />;
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleCancel]);

  const handleChange = (e, name) => {
    const { value } = e.target;

    setMonthlyPayment(prevMonthlyPayment => ({
      ...prevMonthlyPayment,
      [name]: value,
    }));
  };

  const handleChangeFinance = (e, index, name) => {
    const { value } = e.target;

    setFinance(prevFinance =>
      prevFinance.map(item => {
        if (item.temp_id !== index) return item;

        const updatedItem = { ...item };

        if (name === 'price') {
          const formattedValue = formatValueOnChange(value);
          updatedItem[name] = formattedValue;
        } else if (name === 'name') {
          updatedItem[name] = value;
          // Não atualizamos mais o competence aqui
        } else if (name === 'competence') {
          updatedItem[name] = value;
          // Não atualizamos mais o name aqui
        } else {
          updatedItem[name] = value;
        }

        return updatedItem;
      })
    );
  };

  const handleChangeSelect = (selectedOption, name) => {
    setMonthlyPayment(prevMonthlyPayment => ({
      ...prevMonthlyPayment,
      [name]: selectedOption.value,
    }));
  };

  const handleChangeSelectFinance = (selectedOption, index, name) => {
    setFinance(prevFinance =>
      prevFinance.map(item => {
        if (item.temp_id !== index) return item;

        const updatedItem = { ...item, [name]: selectedOption.value };
        return updatedItem;
      })
    );
  };

  const expirationTypeOptions = [
    { value: 1, label: 'Contra apresentação' },
    { value: 2, label: 'Data' },
  ];

  const speciesOptions = [
    { value: 4, label: 'Duplicata de serviço' },
    { value: 2, label: 'Fatura' },
  ];

  const interestTypeOptions = [
    { value: 1, label: '(R$) Valor por dia' },
    { value: 2, label: '(%) ao mês' },
  ];

  const fineTypeOptions = [
    { value: 0, label: 'Não' },
    { value: 1, label: '(R$) Valor fixo' },
    { value: 2, label: '(%) ao mês' },
  ];

  const protestOptions = [
    { value: 1, label: 'Protestar em (x) dias corridos' },
    { value: 2, label: 'Protestar em (x) dias úteis' },
    { value: 3, label: 'Não' },
  ];

  const howToChargeOptions = [
    { value: 0, label: 'Email' },
    { value: 1, label: 'SMS' },
    { value: 2, label: 'WhatsApp' },
    { value: 3, label: 'Correios' },
  ];

  const handleAddNewFinance = useCallback(() => {
    const newFinance = {
      temp_id: uuidv4(),
      id: null,
      eref: '1.018.015',
      eidref: monthlyPayment.id,
      company_id: company.old_id,
      mainid: 1,
      date: new Date(),
      account: '',
      document: null,
      financial: monthlyPayment.financial,
      name: '',
      competence: null,
      price: formatValue(0),
      note: null,
      date_to_allow: null,
      status: false,
    };

    setFinance(prevFinance => [newFinance, ...(prevFinance || [])]);
  }, [monthlyPayment, company.old_id]);

  const handleRemoveFinance = useCallback(async index => {
    setFinance(prevFinance =>
      prevFinance.map(item =>
        item.temp_id === index ? { ...item, status: true } : item
      )
    );
  }, []);

  return (
    <Container>
      <Header>
        <div>
          <FaBarcode />
          <h1>Cobrança - Boletos</h1>
        </div>

        <Link to="/">
          <FaTimes />
        </Link>
      </Header>
      <Controls>
        <button type="button" onClick={confirmSaveForm}>
          <FaSave size={15} color="#44546a" />
          <span>Salvar</span>
        </button>
        <button type="button" onClick={confirmCalcelForm}>
          <FaTimesCircle />
          <span>Cancelar</span>
        </button>
      </Controls>
      {loading ? (
        <TableLoading />
      ) : (
        <Content className="content">
          {monthlyPayment && (
            <BilletForm ref={formRef} onSubmit={handleSave}>
              <h4>Mensalidade</h4>
              <section>
                <Select
                  label="Cliente (Sacado)"
                  name="drawee"
                  className="drawee"
                  options={clientsOptions}
                  placeholder="Selecione um cliente"
                  value={clientsOptions.find(
                    option => option.value === monthlyPayment?.drawee
                  )}
                  onChange={option => handleChangeSelect(option, 'drawee')}
                />
                <Input
                  name="price"
                  className="price"
                  label="Preço (calculado automaticamente)"
                  value={monthlyPayment.price}
                  readOnly
                />
                <Input
                  name="emition_day"
                  className="emition_day"
                  label="Dia de Emissão"
                  value={monthlyPayment.emition_day}
                  onChange={e => handleChange(e, 'emition_day')}
                />
                <Select
                  label="Dados financeiros"
                  name="financial"
                  className="financial_data"
                  options={financialOptions}
                  placeholder="Selecione um dado financeiro"
                  value={financialOptions.find(
                    option => option.value === monthlyPayment?.financial
                  )}
                  onChange={option => handleChangeSelect(option, 'financial')}
                />
                <Input
                  name="employee_limit"
                  className="employee_limit"
                  label="Limite de empregados"
                  type="number"
                  min={0}
                  step={1}
                  value={
                    monthlyPayment.employee_limit !== undefined
                      ? monthlyPayment.employee_limit
                      : company?.employee_limit || 0
                  }
                  onChange={e => {
                    const parsed = parseInt(e.target.value || '0', 10);
                    setMonthlyPayment(prev => ({
                      ...prev,
                      employee_limit: Number.isNaN(parsed)
                        ? 0
                        : Math.max(0, parsed),
                    }));
                  }}
                />
              </section>
              <section>
                <Select
                  label="Tipo de vencimento"
                  name="expiration_type"
                  className="expiration_type"
                  options={expirationTypeOptions}
                  placeholder="Selecione um tipo de vencimento"
                  defaultValue={expirationTypeOptions.find(
                    option => option.value === monthlyPayment?.expiration_type
                  )}
                  onChange={option =>
                    handleChangeSelect(option, 'expiration_type')
                  }
                />
                <Input
                  name="expiration_day"
                  className="expiration_day"
                  label="Dia de Vencimento"
                  value={monthlyPayment.expiration_day}
                  onChange={e => handleChange(e, 'expiration_day')}
                />
                <Select
                  label="Espécie"
                  name="species"
                  className="species"
                  options={speciesOptions}
                  placeholder="Selecione a espécie"
                  value={speciesOptions.find(
                    option => option.value === monthlyPayment?.species
                  )}
                  onChange={option => handleChangeSelect(option, 'species')}
                  isDisabled
                />
              </section>
              <section>
                <Input
                  name="interest"
                  className="interest"
                  label="Juros"
                  defaultValue={monthlyPayment.interest}
                  onChange={e => handleChange(e, 'interest')}
                />
                <Select
                  label="Juros (Tipo)"
                  name="interest_type"
                  className="interest_type"
                  options={interestTypeOptions}
                  placeholder="Selecione a forma de juros"
                  defaultValue={interestTypeOptions.find(
                    option => option.value === monthlyPayment?.interest_type
                  )}
                  onChange={option =>
                    handleChangeSelect(option, 'interest_type')
                  }
                />
                <Input
                  name="fine"
                  className="fine"
                  label="Multa"
                  defaultValue={monthlyPayment.fine}
                  onChange={e => handleChange(e, 'fine')}
                />
                <Select
                  label="Multa (Tipo)"
                  name="fine_type"
                  className="fine_type"
                  options={fineTypeOptions}
                  placeholder="Selecione a forma de multa"
                  defaultValue={fineTypeOptions.find(
                    option => option.value === monthlyPayment?.fine_type
                  )}
                  onChange={option => handleChangeSelect(option, 'fine_type')}
                />
                <Input
                  name="days_to_fine"
                  className="days_to_fine"
                  label="Dias para multar"
                  defaultValue={monthlyPayment.days_to_fine}
                  onChange={e => handleChange(e, 'days_to_fine')}
                />
                <Select
                  label="Protestar"
                  name="protest"
                  className="protest"
                  options={protestOptions}
                  placeholder="Selecione a forma de protesto"
                  defaultValue={protestOptions.find(
                    option => option.value === monthlyPayment?.protest
                  )}
                  onChange={option => handleChangeSelect(option, 'protest')}
                />
                <Select
                  label="Como cobrar"
                  name="how_to_charge"
                  className="how_to_charge"
                  options={howToChargeOptions}
                  placeholder="Selecione como cobrar"
                  defaultValue={howToChargeOptions.find(
                    option => option.value === monthlyPayment?.how_to_charge
                  )}
                  onChange={option =>
                    handleChangeSelect(option, 'how_to_charge')
                  }
                />
              </section>
              <section>
                <Input
                  name="message"
                  className="message"
                  label="Mensagem"
                  defaultValue={monthlyPayment?.message}
                  onChange={e => handleChange(e, 'message')}
                />
              </section>

              <Finances>
                <header>
                  <h4>Eventos</h4>
                  <button type="button" onClick={handleAddNewFinance}>
                    <FaPlus size={10} />
                  </button>
                </header>

                {finance?.length > 0 &&
                  finance
                    .filter(item => !item.status) // Filtra apenas os itens não removidos
                    .map(item => (
                      <Finance key={`${item.temp_id}-${eventsOptions.length}`}>
                        <button
                          type="button"
                          onClick={() => handleRemoveFinance(item.temp_id)}
                        >
                          <FaMinus size={10} />
                        </button>

                        <section>
                          <Select
                            label="Evento"
                            name="account"
                            className="account"
                            options={eventsOptions}
                            placeholder="Selecione um evento"
                            value={
                              eventsOptions.length > 0
                                ? eventsOptions.find(
                                    option => option.value === item.account
                                  )
                                : null
                            }
                            onChange={option =>
                              handleChangeSelectFinance(
                                option,
                                item.temp_id,
                                'account'
                              )
                            }
                          />
                          {/* Campo de competência removido, será enviado como null */}
                          <input type="hidden" name="name" value={item.name} />
                          <Input
                            name="price"
                            className="price"
                            label="Valor"
                            value={item.price}
                            onChange={e =>
                              handleChangeFinance(e, item.temp_id, 'price')
                            }
                          />
                        </section>
                      </Finance>
                    ))}
              </Finances>
            </BilletForm>
          )}
        </Content>
      )}
    </Container>
  );
};

export default Edit;
